"use client";

import { type CSSProperties, type ReactNode, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "../styles/compass.css";

type Anchor = "left" | "right";

export interface InfoTipProps {
  /** Tooltip body content. Can be text or JSX. */
  children: ReactNode;
  /** Pin the tip's left or right edge to the trigger. Defaults to "left". */
  anchor?: Anchor;
  /** Tip width in px. Defaults to 240. */
  width?: number;
  /** Accessible label for the ⓘ trigger. Defaults to "More info". */
  ariaLabel?: string;
}

/**
 * Reusable ⓘ bubble with a portal-rendered tooltip. The portal lets the
 * tooltip escape any ancestor with `overflow: auto/hidden` instead of
 * being clipped at the container edge.
 *
 * Styles live in `compass.css` under `.as-info` and `.as-info-tip`.
 */
export function InfoTip({
  children,
  anchor = "left",
  width = 240,
  ariaLabel = "More info",
}: InfoTipProps) {
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const [tipStyle, setTipStyle] = useState<CSSProperties | null>(null);

  function show() {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const base: CSSProperties = {
      position: "fixed",
      top: rect.bottom + 6,
      width,
      maxWidth: "calc(100vw - 16px)",
      zIndex: 9999,
    };
    if (anchor === "right") {
      base.left = Math.max(8, rect.right - width);
    } else {
      base.left = Math.max(8, rect.left);
    }
    setTipStyle(base);
  }

  const hide = () => setTipStyle(null);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="as-info"
        aria-label={ariaLabel}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
      >
        <svg
          width="11"
          height="11"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden={true}
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M12 16v-4M12 8h.01" />
        </svg>
      </button>
      {tipStyle &&
        createPortal(
          <div className="as-info-tip portaled" style={tipStyle} role="tooltip">
            {children}
          </div>,
          document.body,
        )}
    </>
  );
}
